import type { Component, ComponentTagDictionary, Page } from '@broker-as/acquisit-ui-vue/library'
import type { ValidationError } from '@broker-as/acquisit-ui-vue/library'
import { useLogger, validateComponents } from '@broker-as/acquisit-ui-vue/library'
import { defineStore } from 'pinia'
import { cast, clone, collectByTag, pageHasSemantic } from '@broker-as/acquisit-ui-vue/functions'
import { useProductsStore } from '@/stores/products'
import { cleanComponentsForBackup, setProperties, useComponentsStore } from '@/stores/components'
import { usePersonsStore } from '@/stores/persons'
import { useProtocolStore } from '@/stores/protocol'
import type APIError from '@/lib/APIError.ts'

const log = useLogger('stores/page', useLogger.COLOR_STORE)

export interface CurrentPageStoreState {
	current_page: Page|null
}

export const usePageStore = defineStore('page', {
	state: () => cast<CurrentPageStoreState>({
		current_page: null
	}),
	
	getters: {
		current: state => state.current_page,
		current_uid: state => state.current_page?.data.uid,
		
		components_by_tag: state => {
			if (!state.current_page) {
				return {}
			}
			
			return collectByTag(state.current_page.components)
		},
		
		backup: state => {
			let backup = clone((state as CurrentPageStoreState & { $state: CurrentPageStoreState }).$state) as any
			
			if (backup.current_page?.components?.length) {
				cleanComponentsForBackup(backup.current_page.components)
			}
			
			if (backup.current_page) {
				backup.current_page.data.errors = null
			}
			
			return backup
		}
	},
	
	actions: {
		setCurrentPage(page: Page) {
			this.current_page = page
		},
		
		setErrors(errors: (ValidationError|APIError)[], page?: Page) {
			if (!page) {
				if (!this.current_page) {
					log.always.warnFrom('setErrors', 'No current page is set')
					return
				}
				
				page = this.current_page
			}
			
			page.data.errors = errors as ValidationError[] // TODO: Fix in library to allow other error types as well
		},
		
		async validate(page?: Page): Promise<true> {
			if (!page) {
				if (!this.current_page) {
					log.always.warnFrom('validate', 'No current page is set')
					throw new Error('No current page is set')
				}
				
				page = this.current_page
			}
			
			try {
				const componentsStore = useComponentsStore(),
					productsStore = useProductsStore(),
					personsStore = usePersonsStore(),
					protocolStore = useProtocolStore()
				
				await validateComponents({
					components: page.components,
					componentsByUID: componentsStore.by_uid,
					componentsByTag: componentsStore.by_tag,
					productsByUID: productsStore.by_uid,
					personsByID: personsStore.by_id,
					pagesByUID: protocolStore.pages_by_uid,
					owner: page,
					setProperties
				})
				
				page.data.validated = true
				
				return true
			} catch (e) {
				this.invalidate(page)
				throw e
			}
		},
		
		invalidate(page?: Page) {
			if (!page) {
				if (!this.current_page) {
					log.always.warnFrom('invalidate', 'No current page is set')
					return
				}
				
				page = this.current_page
			}
			
			page.data.validated = false
		},
		
		setEnabled(enabled: boolean, page?: Page) {
			if (!page) {
				if (!this.current_page) {
					log.always.warnFrom('setEnabled', 'No current page is set')
					return
				}
				
				page = this.current_page
			}
			
			if (!page) {
				log.always.warnFrom('setEnabled', 'No page found')
				return
			}
			
			const isProductPage = pageHasSemantic(page, 'product')
			
			if (isProductPage && !enabled) {
				const productsStore = useProductsStore()
				
				let products = productsStore.forPage(page),
					chooser: Component|null = null
				
				// Find the first component that has products - there can only be one chooser per page
				for (let component of page.components) {
					if (Array.isArray(component.properties.products)) {
						chooser = component
						break
					}
				}
				
				for (let product of products) {
					// FIXME Remove soon
					if (!product) {
						continue
					}
					
					if (!product.selected_component_uid || (chooser && product.selected_component_uid == chooser.properties.uid)) {
						productsStore.unselect(product)
					}
				}
			}
			
			page.enabled = enabled
			
			if (!isProductPage && !enabled) {
				page.data.validated = false
			}
		},
		
		restoreBackup(_: Record<any, any>) {
			// Ignore any backups
		}
	}
})